import type { ControllerRenderProps } from 'react-hook-form';
import type {
  EnforceDisabledReason,
  PolicyModeType,
} from '@kirin/api-client/contracts/policies';
import type { PolicySettingsFormData } from '../types';
import { narrowPolicyMode } from '../constants';
import { PolicyModeSelector } from './PolicyModeSelector';
import { useEnforceAvailability } from './enforceAvailability';

interface ModeSettingFieldProps {
  field: ControllerRenderProps<PolicySettingsFormData, string>;
  disabled?: boolean;
  /**
   * Forwarded to the selector so an org without enforcement cannot pick it.
   * Defaults to the form-level `EnforceAvailabilityContext` rather than a
   * literal, so the gate arrives without threading a prop through every
   * `PolicySettingField` call site.
   */
  supportsEnforce?: boolean;
  /**
   * Why Enforce is unavailable. Defaults to the same context as
   * `supportsEnforce` so this selector and the parent policy's agree on the
   * explanation instead of falling back to the generic "Coming Soon".
   */
  enforceDisabledReason?: EnforceDisabledReason;
  enforceTooltip?: string;
  /** `sm` lets the selector sit beside the label instead of under it. */
  size?: 'default' | 'sm';
  /**
   * Overrides only what the selector SHOWS, leaving `field.value` untouched
   * (KIR-4858). Used when the parent policy is Off: the sub-policy's effective
   * mode is Off no matter what is stored, but a settings save is a full
   * replace — writing Off into the form would destroy the admin's stored
   * choice the next time any other field in the same policy is edited. Only
   * meaningful together with `disabled`, which is what keeps the displayed
   * value and the form value from diverging on a click.
   */
  displayValue?: PolicyModeType;
}

/**
 * Mode setting field component (KIR-4426).
 *
 * Renders a nested sub-policy's own Off/Monitor/Enforce control using the same
 * selector as the parent policy, so a sub-policy can enforce while its parent
 * stays on Monitor. Anything unrecognized — absent, or a legacy boolean from
 * an earlier toggle shape of the setting — reads as Off, matching how the
 * backend and the agent narrow the stored value.
 */
export function ModeSettingField({
  field,
  disabled = false,
  supportsEnforce,
  enforceDisabledReason,
  enforceTooltip,
  size = 'default',
  displayValue,
}: ModeSettingFieldProps) {
  const enforceAvailability = useEnforceAvailability();
  const value = displayValue ?? narrowPolicyMode(field.value);

  return (
    <PolicyModeSelector
      value={value}
      onChange={field.onChange}
      disabled={disabled}
      supportsEnforce={supportsEnforce ?? enforceAvailability.supportsEnforce}
      enforceDisabledReason={
        enforceDisabledReason ?? enforceAvailability.enforceDisabledReason
      }
      enforceTooltip={enforceTooltip}
      size={size}
    />
  );
}
